import { useState, useEffect, useCallback } from 'react';
import { emails as mockEmails } from '@/data/mockData';
import type { Email } from '@/types';
import { useTenant } from '@/hooks/useTenant';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

// SyncedEmail extends the app's Email type with the project-linkage columns
// added to emails_sync (project matching/suggestion, conversation threading).
// These are not yet part of the base Email type since most other consumers
// (mock data, etc.) don't have them.
export interface SyncedEmail extends Email {
  project_id: string | null;
  suggested_project_id: string | null;
  match_confidence: number | null;
  matched_via: string | null;
  match_status: string;
  direction: string | null;
  conversation_id: string | null;
  is_read: boolean;
  read_at: string | null;
  // Microsoft Graph message id (emails_sync.external_id) — needed to target
  // the reply-from-inbox action's /users/{mailbox}/messages/{id}/reply call
  // at the correct source message. There is no integration_id column on
  // emails_sync (a synced row doesn't remember which integration_configs
  // row produced it), so the reply flow resolves the tenant's active
  // 'outlook' integration separately at send-time instead of reading it off
  // this row.
  external_id: string;
}

// Default values for the new linkage/read-state fields when converting a
// plain mock Email (which has none of these columns) into a SyncedEmail.
// Mock emails are treated as already-read so they don't inflate the
// unread badge (the badge is driven entirely by real emails_sync rows).
const mockEmailDefaults = {
  project_id: null,
  suggested_project_id: null,
  match_confidence: null,
  matched_via: null,
  match_status: 'unmatched',
  direction: null,
  conversation_id: null,
  is_read: true,
  read_at: null,
  external_id: '',
} as const;

const toSyncedMock = (email: Email): SyncedEmail => ({ ...email, ...mockEmailDefaults });

// Maps an emails_sync row into the Email/SyncedEmail type the rest of this
// component's rendering logic already expects. No read-state, full-body, or
// per-email-sentiment columns exist yet, so those fields get fixed
// defaults — see the migration/brief for why each default was chosen.
const mapSyncedEmail = (row: {
  id: string;
  external_id: string | null;
  sender_name: string | null;
  sender_email: string | null;
  subject: string | null;
  body_preview: string | null;
  received_at: string | null;
  escalation_flag: boolean | null;
  project_id: string | null;
  suggested_project_id: string | null;
  match_confidence: number | null;
  matched_via: string | null;
  match_status: string | null;
  direction: string | null;
  conversation_id: string | null;
  is_read: boolean | null;
  read_at: string | null;
}): SyncedEmail => ({
  id: row.id,
  external_id: row.external_id ?? '',
  from: row.sender_name || 'Unknown Sender',
  fromEmail: row.sender_email || '',
  subject: row.subject || '(no subject)',
  preview: row.body_preview || '',
  body: row.body_preview || '',
  timestamp: row.received_at || new Date().toISOString(),
  isRead: row.is_read ?? true,
  isStarred: false,
  sentiment: { label: 'neutral', score: 0.5, confidence: 0 },
  escalationLevel: row.escalation_flag ? 'L3' : 'L1',
  extractedTasks: [],
  priority: row.escalation_flag ? 'high' : 'medium',
  project_id: row.project_id ?? null,
  suggested_project_id: row.suggested_project_id ?? null,
  match_confidence: row.match_confidence ?? null,
  matched_via: row.matched_via ?? null,
  match_status: row.match_status ?? 'unmatched',
  direction: row.direction ?? null,
  conversation_id: row.conversation_id ?? null,
  is_read: row.is_read ?? true,
  read_at: row.read_at ?? null,
});

// Loads real emails_sync rows. No realtime subscription (email content is
// sensitive — emails_sync is deliberately excluded from the realtime
// publication, same reasoning as meetings_sync), so this loads on mount
// and via the manual "Refresh" button (exposed here as `reload`).
//
// `projectFilter` scopes the query to a single project's newest 50 rows
// (`.eq('project_id', projectFilter)` applied before `.limit(50)`) rather
// than the global newest 50. Pass null (the default) for the old
// unfiltered global-newest-50 behavior — e.g. for a dropdown that needs to
// see every project with at least one linked email, which a
// project-filtered query can't discover on its own.
export const useEmails = (projectFilter: string | null = null) => {
  const { isDemoTenant, demoMockDataEnabled } = useTenant();
  const showMock = isDemoTenant && demoMockDataEnabled;
  const [emails, setEmails] = useState<SyncedEmail[]>(() =>
    showMock ? mockEmails.map(toSyncedMock) : []
  );
  const [isLoadingEmails, setIsLoadingEmails] = useState(true);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setIsLoadingEmails(true);
    try {
      let query = supabase
        .from('emails_sync')
        .select(
          'id, external_id, subject, sender_name, sender_email, body_preview, received_at, escalation_flag, project_id, suggested_project_id, match_confidence, matched_via, match_status, direction, conversation_id, is_read, read_at'
        );

      if (projectFilter) {
        query = query.eq('project_id', projectFilter);
      }

      const { data, error } = await query
        .order('received_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const synced = (data || []).map(mapSyncedEmail);
      // Real synced rows take priority; mock data is a demo-tenant-only
      // fallback when there's nothing real to show yet (mirrors
      // MeetingHub.tsx's loadMeetings pattern). Mock data has no real
      // project_id, so it's only used as a fallback for the unfiltered
      // (projectFilter === null) query.
      const all =
        synced.length > 0
          ? synced
          : (showMock && !projectFilter ? mockEmails.map(toSyncedMock) : []);
      setEmails(all);
    } catch (error) {
      console.error('Failed to load emails:', error);
      const mock = showMock && !projectFilter ? mockEmails.map(toSyncedMock) : [];
      setEmails(mock);
      toast({
        title: 'Could not load Outlook emails',
        description: 'Check that Microsoft 365 is connected and the Outlook integration has Mail.Read permission. Showing cached/demo data in the meantime.',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingEmails(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMock, projectFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Exposed so callers can apply a small optimistic local patch (e.g.
  // flipping is_read/isRead after markEmailRead succeeds) without waiting
  // on a full reload() round-trip.
  return { emails, isLoadingEmails, reload: load, setEmails };
};
